// File: packages/calculator/components/FeatureGroup.tsx

import React, { useState, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ModuleGroup } from '@dopomogai/types';
import { useCalculator } from '@dopomogai/agent-kit';
import { buildFeatureIndex, getFeatureDisabledReason } from '@dopomogai/agent-kit';
import { fmtMoney } from '@dopomogai/agent-kit';
import { addWithDependencies } from '@dopomogai/agent-kit';
import { FeatureItem } from './FeatureItem';

type Props = {
  group: ModuleGroup;
  hideAdvanced?: boolean;
};

/**
 * A collapsible container for a group of related features/modules.
 * It displays a summary of the group and renders a list of FeatureItem components.
 */
export function FeatureGroup({ group, hideAdvanced = false }: Props) {
  const { spec, state, actions } = useCalculator();
  const [isOpen, setIsOpen] = useState(true);

  const featureIndex = useMemo(() => buildFeatureIndex(spec.groups), [spec.groups]);
  const platform = useMemo(
    () => spec.platforms.find((p) => p.id === state.platformId) || spec.platforms[0],
    [spec.platforms, state.platformId]
  );

  const visibleFeatures = useMemo(() => {
    const features = group.features || [];
    if (hideAdvanced) {
      return features.filter((f) => f.visibility !== 'advanced');
    }
    return features;
  }, [group.features, hideAdvanced]);

  const selectedInGroup = useMemo(
    () => visibleFeatures.filter((f) => state.selectedModuleIds.has(f.id)),
    [visibleFeatures, state.selectedModuleIds]
  );

  const subtotal = useMemo(
    () => selectedInGroup.reduce((sum, f) => sum + f.priceUSD, 0),
    [selectedInGroup]
  );

  // Add all required dependencies, then select the requested feature
  const handleFixSelection = useCallback((featureId: string) => {
    const target = featureIndex[featureId];
    if (!target) return;
    const { nextSelected } = addWithDependencies(state.selectedModuleIds, target, featureIndex);
    // Toggle on any missing items in the closure
    nextSelected.forEach((id) => {
      if (!state.selectedModuleIds.has(id)) {
        actions.toggleModule(id);
      }
    });
  }, [actions, state.selectedModuleIds, featureIndex]);

  return (
    <section className="border-2 border-black rounded-xl bg-slate-50">
      <button
        type="button"
        className="w-full flex items-center justify-between p-4"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-controls={`feature-group-content-${group.id}`}
      >
        <div className="flex items-center gap-4">
          <h4 className="font-extrabold text-lg text-slate-900">{group.label}</h4>
          <span className="text-sm font-semibold text-slate-600">
            {selectedInGroup.length} selected
            {subtotal > 0 && ` • ${fmtMoney(subtotal, spec.currency)}`}
          </span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {isOpen && (
        <div id={`feature-group-content-${group.id}`} className="p-4 border-t-2 border-black space-y-3">
          {visibleFeatures.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-2">No features to display in this view.</p>
          )}
          {visibleFeatures.map((feature) => (
            <FeatureItem
              key={feature.id}
              feature={feature}
              checked={state.selectedModuleIds.has(feature.id)}
              disabledReason={getFeatureDisabledReason(feature, platform, state.selectedModuleIds, featureIndex)}
              onToggle={() => actions.toggleModule(feature.id)}
              onFix={() => handleFixSelection(feature.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}